import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL } from "@/seo/data";
import { Sparkle } from "@phosphor-icons/react";

export const ProcessShowcase = ({
  eyebrow, title, intro, steps, Scene,
  accent = "#2563eb", soft = "#eff6ff", softBorder = "#dbeafe",
  videos = [], className = "bg-white border-t border-slate-200",
  testid = "process-showcase",
}) => {
  const [step, setStep] = useState(0);
  const [inView, setInView] = useState(false);
  const [reduced, setReduced] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false);
    const el = rootRef.current;
    if (!el || !("IntersectionObserver" in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || reduced) return undefined;
    const t = setTimeout(() => setStep((s) => (s + 1) % steps.length), 5200);
    return () => clearTimeout(t);
  }, [inView, reduced, step, steps.length]);

  const activeVideos = videos.filter((v) => v && v.src);

  return (
    <section ref={rootRef} className={`py-14 ${className}`} data-testid={testid}>
      {activeVideos.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(activeVideos.map((v) => ({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: v.title,
              description: v.desc,
              thumbnailUrl: SITE_URL + v.poster,
              contentUrl: SITE_URL + v.src,
              uploadDate: v.uploadDate,
              duration: v.duration || "PT8S",
              publisher: { "@type": "Organization", name: "Servus Autoglas — AutoGlas Erol GmbH" },
            })))}
          </script>
        </Helmet>
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <span
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border"
          style={{ backgroundColor: soft, borderColor: softBorder, color: accent }}
        >
          <Sparkle size={14} weight="fill" /> {eyebrow}
        </span>
        <h2 className="mt-4 text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{title}</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">{intro}</p>

        <div className="mt-8 grid lg:grid-cols-[1fr,1.15fr] gap-5 lg:gap-8 items-stretch">
          {/* Schritte — Inhalte immer im DOM */}
          <ol className="order-2 lg:order-1 space-y-2.5" data-testid={`${testid}-steps`}>
            {steps.map((s, i) => {
              const active = i === step;
              return (
                <li key={s.t}>
                  <button
                    onClick={() => setStep(i)}
                    aria-current={active ? "step" : undefined}
                    data-testid={`${testid}-step-${i}`}
                    className={`w-full text-left rounded-2xl border p-4 transition-colors ${active ? "bg-white shadow-sm" : "bg-white/60 border-slate-200 hover:border-slate-300"}`}
                    style={active ? { borderColor: accent } : undefined}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                        style={active ? { backgroundColor: accent, color: "#fff" } : { backgroundColor: "#f1f5f9", color: "#64748b" }}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className={`font-bold text-sm ${active ? "text-slate-900" : "text-slate-600"}`}>{s.t}</p>
                        <p className={`mt-1 text-sm leading-relaxed ${active ? "text-slate-600" : "text-slate-400"}`}>{s.d}</p>
                      </div>
                    </div>
                    {active && (
                      <div className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                        <div
                          key={step}
                          className="h-full rounded-full px-anim"
                          style={{ backgroundColor: accent, width: reduced || !inView ? "100%" : undefined, animation: reduced || !inView ? "none" : "pxProgress 5.2s linear forwards" }}
                        />
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Animierte Szene */}
          <div
            className="order-1 lg:order-2 rounded-3xl border overflow-hidden flex items-center justify-center p-4 sm:p-6"
            style={{ backgroundColor: soft, borderColor: softBorder }}
            data-testid={`${testid}-scene`}
          >
            <Scene step={step} accent={accent} />
          </div>
        </div>

        {/* Videobereiche */}
        {activeVideos.length > 0 && (
          <div className={`mt-8 grid gap-5 ${activeVideos.length > 1 ? "md:grid-cols-2" : ""}`}>
            {activeVideos.map((v) => (
              <figure key={v.src} className="m-0">
                <div className="rounded-3xl overflow-hidden bg-slate-900 shadow-sm">
                  <video
                    controls
                    playsInline
                    preload="none"
                    poster={v.poster}
                    className="w-full aspect-video object-cover"
                    data-testid={`${testid}-video`}
                  >
                    <source src={v.src} type="video/mp4" />
                  </video>
                </div>
                <figcaption className="mt-2 text-xs text-slate-500 text-center">
                  {v.caption} · KI-generierte Visualisierung
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProcessShowcase;
